// Recurring Job Template Item editor, Add and last-item delete guard.
import { inject, Injector } from '@angular/core';
import { ActivityEntityName, JobTemplateCode, RecurringJobTemplateItemCommandId } from '@jms/model';
import { resolveItemFromRow } from '@workbuddy/entities/shared/items';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { mergeEntityBoardConfig } from '@workbuddy/services/entities/merge.entity.board.config';
import type { EntityManifestBoardProfile } from '@workbuddy/services/entities/entity.manifest.surface';
import { createEmbeddedItemAddAdapter, embeddedItemBoardProfile, loadEmbeddedItemCatalogueFacts, lockedEmbeddedItemReadonly, type EmbeddedItemProfile } from '@workbuddy/surfaces/embedded-item';
import type { EntityBoardContext } from '@workbuddy/ui/v2/blocks/entity.board';
import { lastValueFrom } from 'rxjs';
import { RECURRING_JOB_TEMPLATE_ITEM_QUERY_KEY, RECURRING_JOB_TEMPLATE_ITEM_SCOPE, type RecurringJobTemplateItemBoardParams } from '@workbuddy/entities/work.recurring.job.template.item/recurring.job.template.item.model';

export function recurringItemBoardProfile(_base: EntityManifestBoardProfile, page: EntityBoardContext): Partial<EntityManifestBoardProfile> {

  const editor = {
    entity: ActivityEntityName.RecurringJobTemplateItem,
    lineEntity: ActivityEntityName.RecurringJobTemplateCode,
    scope: RECURRING_JOB_TEMPLATE_ITEM_SCOPE,
    queryKey: RECURRING_JOB_TEMPLATE_ITEM_QUERY_KEY,
    fields: {},
    layout: () => 'stacked',

    load: async (row, params, injector) => {
      const line = resolveItemFromRow(row, RECURRING_JOB_TEMPLATE_ITEM_SCOPE) as JobTemplateCode | null;
      if (!line?._id || !line.name) return null;

      const facts = await loadEmbeddedItemCatalogueFacts(line.name, injector);
      return {
        line,
        uoms: facts.uoms,
        readonly: lockedEmbeddedItemReadonly(!params?.writable(), facts.canOverrideDesc),
        hide: facts.canOverrideDesc ? {} : { description: true },
      };
    },

    // The pricebook is applied when a job is GENERATED from this template (the scheduler carries
    // `template.priceBook` onto the job create request), so an absent price is the instruction to resolve it then.
    priceReset: 'server',

    // `RecurringJobRepository.updateCode` is a plain write with no rule of its own.
    serverZeroesForcedPrices: false,

    save: async (line, patch, params, injector) => {
      const recurringJobId = params?.recurringJob()?._id;
      if (!recurringJobId) throw new Error('The owning recurring job is required to save this item.');
      await lastValueFrom(injector.get(EntityDataServiceRaw).executeCommand(ActivityEntityName.RecurringJobTemplateItem, RecurringJobTemplateItemCommandId.UpdateItem, { recurringJobId, itemId: line._id, patch }));
    },

    afterWrite: params => params?.itemsChanged(),

    // 56, not the 40px default: the money cell stacks the extension over the unit price, which a 40px
    // row crowds. Same fixed height the SOR board uses for the same cell.
    board: { rowHeight: 56 },

    delete: {

      // Legacy refuses the same way — acknowledge-only, because there is nothing for the user to confirm.
      guard: (_line, params) => params && params.itemCount() <= 1 && params.recurringJob()?.turnOn
        ? 'At least one item should be added to the recurring job. Please add new items before deleting.'
        : null,
      confirm: line => ({ title: 'Delete item', message: `Delete '${line.name}'? Jobs already generated keep the items they were created with.` }),
      write: async (line, params, injector) => {
        const recurringJobId = params?.recurringJob()?._id;
        if (!recurringJobId) return;
        await lastValueFrom(injector.get(EntityDataServiceRaw).executeCommand(ActivityEntityName.RecurringJobTemplateItem, RecurringJobTemplateItemCommandId.DeleteItem, { recurringJobId, itemId: line._id }));
      },
    },
  } satisfies EmbeddedItemProfile<JobTemplateCode, RecurringJobTemplateItemBoardParams>;

  const entityBoard = embeddedItemBoardProfile(editor);

  const params = page.params as RecurringJobTemplateItemBoardParams;
  const injector = inject(Injector);
  const entityData = inject(EntityDataServiceRaw);

  if (!params) return entityBoard;

  return mergeEntityBoardConfig(entityBoard, {
    header: {
      hideAdd: !page.header?.parentId || !params.writable(),
    },
    params: {
      itemBrowser: () => createEmbeddedItemAddAdapter({
        title: 'Browse Items',
        canAdd: () => !!params.recurringJob()?._id && params.writable(),
        write: lines => {
          const recurringJobId = params.recurringJob()?._id;
          if (!recurringJobId) throw new Error('A recurring job is required to add items.');

          return lastValueFrom(entityData.executeCommand<JobTemplateCode[]>(
            ActivityEntityName.RecurringJobTemplateItem,
            RecurringJobTemplateItemCommandId.AddItems,
            { recurringJobId, items: lines },
          ));
        },
        queryKey: RECURRING_JOB_TEMPLATE_ITEM_QUERY_KEY,
      }, injector),
    },
  }) as Partial<EntityManifestBoardProfile>;
}
